import { useEffect, useRef, useCallback } from 'react'
import { HelpCircle, X } from 'lucide-react'
import { useHelpWidget } from '../hooks/useHelpWidget'
import { WidgetTabBar } from './help-widget/WidgetTabBar'
import { WidgetHomeTab } from './help-widget/WidgetHomeTab'
import { WidgetHelpTab } from './help-widget/WidgetHelpTab'
import { WidgetChat } from './help-widget/WidgetChat'
import { WidgetFeedbackType } from './help-widget/WidgetFeedbackType'
import { WidgetFeedbackForm } from './help-widget/WidgetFeedbackForm'
import type { FeedbackTypeOption } from './help-widget/WidgetFeedbackType'

export default function HelpWidget() {
  const {
    isOpen,
    isExpanded,
    openWidget,
    closeWidget,
    activeTab,
    switchTab,
    overlay,
    goHomeTab,
    goGuide,
    collapseGuide,
    guideIndex,
    currentSection,
    setCurrentSection,
    pendingAnchor,
    setPendingAnchor,
    sectionContent,
    loading,
    indexLoading,
    goFeedbackType,
    goFeedbackForm,
    dismissOverlay,
  } = useHelpWidget()

  const panelRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleToggle = useCallback(() => {
    if (isOpen) {
      closeWidget()
    } else {
      openWidget()
    }
  }, [isOpen, openWidget, closeWidget])

  const handleSwitchToHelp = useCallback(
    (section?: string, anchor?: string) => {
      goGuide(section, anchor)
    },
    [goGuide]
  )

  const handleStartFeedback = useCallback(() => {
    goFeedbackType()
  }, [goFeedbackType])

  const handleSelectFeedbackType = useCallback((type: FeedbackTypeOption) => {
    goFeedbackForm(type)
  }, [goFeedbackForm])

  const handleFeedbackSuccess = useCallback(() => {
    dismissOverlay()
    goHomeTab()
  }, [dismissOverlay, goHomeTab])

  const handleFeedbackTypeBack = useCallback(() => {
    dismissOverlay()
  }, [dismissOverlay])

  const handleChatFeedback = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail || {}
    const feedbackType = detail.feedbackType || 'bug'
    const prefill = detail.title || detail.description
      ? { title: detail.title, description: detail.description }
      : undefined
    goFeedbackForm(feedbackType, prefill)
  }, [goFeedbackForm])

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        closeWidget()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, closeWidget])

  // Escape to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeWidget()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, closeWidget])

  // Listen for feedback events from chat widget
  useEffect(() => {
    window.addEventListener('help-widget:feedback', handleChatFeedback)
    return () => window.removeEventListener('help-widget:feedback', handleChatFeedback)
  }, [handleChatFeedback])

  // Panel classes: normal vs expanded
  const panelClasses = isExpanded
    ? 'fixed z-50 bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ease-in-out inset-4 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[700px] sm:max-w-[calc(100vw-2rem)] sm:h-[calc(100vh-5rem)] sm:max-h-[calc(100vh-5rem)]'
    : 'fixed z-50 bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ease-in-out inset-4 sm:inset-auto sm:bottom-20 sm:right-6 sm:w-[420px] sm:max-w-[calc(100vw-2rem)] sm:h-[600px] sm:max-h-[calc(100vh-6rem)]'

  return (
    <>
      {/* Trigger button - always visible */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        aria-label={isOpen ? 'Close help' : 'Open help'}
      >
        {isOpen ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Panel */}
      {isOpen && (
        <div ref={panelRef} className={panelClasses}>
          {/* Mobile close button — visible only on small screens */}
          <button
            onClick={closeWidget}
            className="sm:hidden absolute top-2 right-2 z-50 w-8 h-8 rounded-full bg-muted hover:bg-muted-foreground/20 flex items-center justify-center transition-colors"
            aria-label="Close help"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Overlay screens (feedback) — shown OVER tab content */}
          {overlay?.screen === 'feedback-type' && (
            <WidgetFeedbackType
              onBack={handleFeedbackTypeBack}
              onClose={closeWidget}
              onSelectType={handleSelectFeedbackType}
            />
          )}
          {overlay?.screen === 'feedback-form' && (
            <WidgetFeedbackForm
              feedbackType={overlay.feedbackType as FeedbackTypeOption}
              initialTitle={overlay.prefill?.title}
              initialDescription={overlay.prefill?.description}
              onBack={goFeedbackType}
              onClose={closeWidget}
              onSuccess={handleFeedbackSuccess}
            />
          )}

          {/* Tab content — hidden when overlay is active */}
          {!overlay && (
            <>
              {activeTab === 'home' && (
                <WidgetHomeTab
                  onSwitchToHelp={handleSwitchToHelp}
                  onStartFeedback={handleStartFeedback}
                />
              )}
              {activeTab === 'chat' && (
                <WidgetChat isWidgetOpen={isOpen} />
              )}
              {activeTab === 'help' && (
                <WidgetHelpTab
                  isExpanded={isExpanded}
                  guideIndex={guideIndex}
                  currentSection={currentSection}
                  setCurrentSection={setCurrentSection}
                  pendingAnchor={pendingAnchor}
                  setPendingAnchor={setPendingAnchor}
                  sectionContent={sectionContent}
                  loading={loading}
                  indexLoading={indexLoading}
                  onClose={closeWidget}
                  onExpand={goGuide}
                  onCollapse={collapseGuide}
                  onReportBug={() => goFeedbackForm('bug')}
                />
              )}
              {/* Tab bar - always at bottom when no overlay */}
              <WidgetTabBar activeTab={activeTab} onTabChange={switchTab} />
            </>
          )}
        </div>
      )}
    </>
  )
}
